import { useEffect, useRef, useState } from "react";
import { PageHeader } from "@/components/layout/PageHeader";
import { AnimatePresence, motion, MotionConfig } from "framer-motion";
import {
  User,
  Building2,
  Users,
  CreditCard,
  Bell,
  Code2,
  AlertTriangle,
  Settings,
} from "lucide-react";
import { SettingsNav, type SettingsNavItem } from "./components/SettingsNav";
import { useSettings } from "./hooks/useSettings";
import { ProfileSection } from "./components/ProfileSection";
import { BusinessProfileSection } from "./components/BusinessProfileSection";
import { TeamSection } from "./components/TeamSection";
import { BillingSection } from "./components/BillingSection";
import { NotificationsSection } from "./components/NotificationsSection";
import { DeveloperSection } from "./components/DeveloperSection";
import { DangerZoneSection } from "./components/DangerZoneSection";
import { sectionVariants } from "./components/motion";
import { initialUserProfile, initialBusinessProfile, type UserProfile, type BusinessProfile } from "./mockSettings";

export type SectionId =
  | "profile"
  | "business"
  | "team"
  | "billing"
  | "notifications"
  | "developer"
  | "danger";

const NAV_ITEMS: SettingsNavItem[] = [
  { id: "profile", label: "Profile", icon: User },
  { id: "business", label: "Business profile", icon: Building2 },
  { id: "team", label: "Team & roles", icon: Users },
  { id: "billing", label: "Billing & plan", icon: CreditCard },
  { id: "notifications", label: "Notifications", icon: Bell },
  { id: "developer", label: "API & webhooks", icon: Code2 },
  { id: "danger", label: "Danger zone", icon: AlertTriangle },
];

/**
 * Settings — Stripe/Linear-style layout: a sticky section nav (horizontal
 * scroll strip on mobile) beside a single content pane. Section switching
 * is local state, not routing, since this page doesn't need deep-linkable
 * sub-routes yet. Profile and business identity are lifted here since
 * they're the two "core identity" objects most likely to be read by other
 * future surfaces; every other section owns its own local mock state
 * because nothing outside that section depends on it.
 */
export function SettingsPage() {
  const [activeSection, setActiveSection] = useState<SectionId>("profile");
  const [userProfile, setUserProfile] = useState<UserProfile>(initialUserProfile);
  const [businessProfile, setBusinessProfile] = useState<BusinessProfile>(initialBusinessProfile);

  // Load real profile (/auth/me) + business (/chatbot/config), hydrate once,
  // then persist edits. Team/billing/etc. stay local until they have a backend.
  const { userProfile: loadedProfile, businessProfile: loadedBusiness, isLoading, saveProfile, saveBusiness } =
    useSettings();
  const hydratedRef = useRef(false);
  useEffect(() => {
    if (isLoading || hydratedRef.current) return;
    setUserProfile(loadedProfile);
    setBusinessProfile(loadedBusiness);
    hydratedRef.current = true;
  }, [isLoading, loadedProfile, loadedBusiness]);

  const patchUserProfile = (patch: Partial<UserProfile>) => {
    setUserProfile((prev) => ({ ...prev, ...patch }));
    saveProfile(patch);
  };

  const patchBusinessProfile = (patch: Partial<BusinessProfile>) => {
    setBusinessProfile((prev) => ({ ...prev, ...patch }));
    saveBusiness(patch);
  };

  const renderSection = () => {
    switch (activeSection) {
      case "profile":
        return <ProfileSection profile={userProfile} onChange={patchUserProfile} />;
      case "business":
        return <BusinessProfileSection profile={businessProfile} onChange={patchBusinessProfile} />;
      case "team":
        return <TeamSection />;
      case "billing":
        return <BillingSection />;
      case "notifications":
        return <NotificationsSection />;
      case "developer":
        return <DeveloperSection />;
      case "danger":
        return <DangerZoneSection />;
      default:
        return null;
    }
  };

  return (
    <MotionConfig reducedMotion="user">
      <div className="flex-1 bg-transparent p-4 md:p-6">
        <div className="mx-auto max-w-7xl space-y-4 md:space-y-5">
        <div className="flex flex-col gap-6 md:flex-row md:items-start">
          <SettingsNav items={NAV_ITEMS} activeId={activeSection} onChange={setActiveSection} />

          <div className="min-w-0 flex-1">
            <AnimatePresence mode="wait">
              <motion.div
                key={activeSection}
                variants={sectionVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
              >
                {renderSection()}
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
        </div>
      </div>
    </MotionConfig>
  );
}
